import { AuthorizationService } from './../../../services/authorization.service';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Configuration } from '../../../../assets/Configuration';
import * as AspNetData from 'devextreme-aspnet-data-nojquery';
import { Observable, Subscription } from 'rxjs';
import { filter, map, shareReplay, first } from 'rxjs/operators';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { OrganisationUnitTypeModel } from 'src/app/models/organisation-unit/organisation-unit-type.model';
import { OrganisationUnitModel } from 'src/app/models/organisation-unit/organisation-unit.model';
import { TrainingGroupService } from 'src/app/services/training-group.service';
import { OrganisationUnitService } from 'src/app/services/organisation-unit.service';
import DataSource from 'devextreme/data/data_source';
import { ViewType } from 'devextreme/ui/scheduler';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { TrainingTypeModel } from 'src/app/models/training/training-type.model';
import { getTrainingTypeLabel } from 'src/app/utils/training-type.utils';
import { getTrainingLocationLabel } from 'src/app/utils/training-location.utils';
import { TrainingLocationModel } from 'src/app/models/training/training-location.model';

const TEXT_COLOR = '#ffffff';
const SOURCE_BACKGROUND_COLOR = '#dddddd';
const ACCEPTED_BACKGROUND_COLOR = '#28900E';
const SOURCE_TEXT_COLOR = '#393939';
const NORMAL_BACKGROUND_COLOR = '#585E98';

@Component({
  selector: 'app-training-calendar',
  templateUrl: './training-calendar.component.html',
  styleUrls: ['./training-calendar.component.scss'],
  standalone: false
})
export class TrainingCalendarComponent implements OnInit {
  private subs = new Subscription();

  schedulerData: any;
  currentDate: Date = new Date();
  currentView: ViewType = 'month';
  year: string = new Date().getFullYear().toString();
  accessToken!: string;

  isSuperAdmin: boolean = false;
  isMobile: boolean = false;

  filterForm = new UntypedFormGroup({
    organisationUnitLocationIds: new UntypedFormControl(),
    trainingGroupIds: new UntypedFormControl(),
  });

  @ViewChild('dayCellTmpl') dayCellTmpl!: ElementRef<HTMLElement>;

  private organisationUnits$: Observable<OrganisationUnitModel[]> =
    this.organisationUnitService.getAllOrganisationUnits().pipe(shareReplay(1));
  locations$ = this.organisationUnits$.pipe(
    map((organisationUnits) =>
      organisationUnits.filter(
        (organisationUnit) =>
          organisationUnit.type == OrganisationUnitTypeModel.Location
      )
    )
  );
  trainingGroups$ = this.trainingGroupService.getAllTrainingGroups();

  get schedulerCurrentDate(): Date {
    return this.currentView.toLowerCase() !== 'week' 
      ? this.agendaStartDate 
      : this.currentDate;
  }

  agendaStartDate!: Date
  agendaDuration!: number;

  ngOnInit(): void {
    this.checkScreenSize();
    window.addEventListener('resize', () => this.checkScreenSize());

    this.agendaStartDate = new Date(
      this.currentDate.getFullYear(), this.currentDate.getMonth(), 1
    );

    const lastDayOfMonth = new Date(
      this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 0
    );
    this.agendaDuration = lastDayOfMonth.getDate();  
  }

  checkScreenSize() {
    this.isMobile = window.innerWidth <= 767;
    if(this.isMobile) {
      this.currentView = 'agenda';
    }
  }

  get schedulerHeight(): number | string {
    const currentView =  this.currentView.toLowerCase();

    // full height for month and week views
    if (currentView === 'month' || currentView === 'week') {
      return '100%';
    }

    // fixed height for agenda or others
    return 750;
  }

  onCurrentDateChange(newDate: any) {
    this.currentDate = newDate;

    if (this.currentView.toLowerCase() !== 'agenda') {
      return;
    }

    const currentStart = this.agendaStartDate;
    const currentEnd = new Date(currentStart.getFullYear(), currentStart.getMonth(), this.agendaDuration);

    let year = currentStart.getFullYear();
    let month = currentStart.getMonth();

    if (newDate < currentStart) {
      month -= 1;
    } else if (newDate > currentEnd) {
      month += 1;
    }

    if (month < 0) {
      month = 11;
      year -= 1;
    } else if (month > 11) {
      month = 0;
      year += 1;
    }

    this.agendaStartDate = new Date(year, month, 1);
    this.agendaDuration = new Date(year, month + 1, 0).getDate();
  }

  constructor(
    private authorizationService: AuthorizationService,
    private trainingGroupService: TrainingGroupService,
    private organisationUnitService: OrganisationUnitService,
    readonly translateService: TranslateService,
    private router: Router
  ) {
    this.authorizationService
      .getCurrentPerson()
      .pipe(first())
      .subscribe((currentUser) => {
        if (currentUser!.person.role.name === 'SuperAdmin') {
          this.isSuperAdmin = true;
        }

        const url = Configuration.getApiBaseUrl() + '/trainingcalendar';
        const sub = this.authorizationService.accessToken$
          .pipe(
            filter((accessToken: string | null) => accessToken != null),
            map((accessToken: string | null) => accessToken as string)
          )
          .subscribe((accessToken) => {
            this.accessToken = accessToken;

            const dataSourceObject: any = {
              store: AspNetData.createStore({
                key: 'Id',
                loadUrl: `${url}`,
                onBeforeSend(method, ajaxOptions) {
                  ajaxOptions.headers = {
                    Authorization: 'Bearer ' + accessToken,
                  };
                },
              }),
            }

            if (!this.isSuperAdmin) {
              dataSourceObject.filter = ['isDeployed', '=', true]
            }

            this.schedulerData = new DataSource(dataSourceObject);
          });
        this.subs.add(sub);
        this.filterForm.valueChanges.subscribe(() =>
          this.updateSchedulerData()
        );
      });
  }

  getTrainingType(trainingType: TrainingTypeModel) {
    return getTrainingTypeLabel(trainingType, this.translateService)
  }

  getTrainingLocation(location: TrainingLocationModel) {
    return getTrainingLocationLabel(location);
  }

  // setSchedulerData(): void {
  //   const url =
  //     Configuration.getApiBaseUrl() + `/trainingcalendar/by-year/${this.year}`;
  //   const token = this.accessToken;

  //   this.schedulerData = new DataSource({
  //     store: AspNetData.createStore({
  //       key: 'Id',
  //       loadUrl: `${url}`,
  //       onBeforeSend(method, ajaxOptions) {
  //         ajaxOptions.headers = {
  //           Authorization: 'Bearer ' + token,
  //         };
  //       },
  //     }),
  //   });
  // }

  updateSchedulerData(): void {
    const organisationUnitLocationFilter = this.filterForm.controls
      .organisationUnitLocationIds.value
      ? this.filterForm.controls.organisationUnitLocationIds.value
      : [];
    const trainingGroupFilter = this.filterForm.controls.trainingGroupIds.value
      ? this.filterForm.controls.trainingGroupIds.value
      : [];

    const locationFilterExpression: any[] = [];
    const trainingGroupFilterExpression: any[] = [];

    for (let i = 0; i < organisationUnitLocationFilter.length; i++) {
      locationFilterExpression.push([
        'LocationIds',
        '=',
        organisationUnitLocationFilter[i],
      ]);
      if (i != organisationUnitLocationFilter.length - 1) {
        locationFilterExpression.push('or');
      }
    }
    for (let i = 0; i < trainingGroupFilter.length; i++) {
      trainingGroupFilterExpression.push([
        'TrainingGroupIds',
        '=',
        trainingGroupFilter[i],
      ]);
      if (i != trainingGroupFilter.length - 1) {
        trainingGroupFilterExpression.push('or');
      }
    }

    if (
      locationFilterExpression.length > 0 &&
      trainingGroupFilterExpression.length > 0
    ) {
      this.schedulerData.filter([
        locationFilterExpression,
        'and',
        trainingGroupFilterExpression,
      ]);
    } else if (locationFilterExpression.length > 0) {
      this.schedulerData.filter(locationFilterExpression);
    } else {
      this.schedulerData.filter(trainingGroupFilterExpression);
    }
    this.schedulerData.load();
  }

  customizeDateNavigatorText = (e: any) => {
    const startDay = e.startDate.getDate();
    const currentLanguage = this.translateService.currentLang;

    const endDay = e.endDate.getDate();
    const shortMonAndYear = e.endDate.toLocaleString(currentLanguage, {
      month: 'short',
      year: 'numeric',
    });

    const currentView =  this.currentView.toLowerCase();

    if (currentView === 'week') {
      const days = startDay + '-' + endDay;
      return days + ' ' + shortMonAndYear;
    }

    if (currentView === 'month') {
      return e.startDate.toLocaleString(currentLanguage, {
        year: 'numeric',
        month: 'long',
      });
    }

    if (currentView === 'agenda') {
      return e.startDate.toLocaleString(currentLanguage, {
        month: 'long',
        year: 'numeric',
      });
    }

    return startDay + ' ' + shortMonAndYear;
  };

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  formatTime(time: string | null) {
    if (!time) {
      return '';
    }

    const minutes = time.split(':')[1];
    if (minutes == '00') {
      return time.split(':')[0];
    } else return time;
  }

  redirectToDetailPage(event: any): void {
    const appointmentId = event.appointmentData.id;
    this.router.navigate(['/my-trainings/training/' + appointmentId], {
      queryParams: { isFromCalendar: true },
    });
  }

  dayCounter = new Map<string, number>();
  
  colorizeAppointments(e: any) {
    const appointment = e.appointmentData;

    const element = e.appointmentElement as HTMLElement;
    element.classList.add('appointment-container');

    let bgColor = '';
    let textColor = '';

    if(appointment.isAccepted) {
      bgColor = ACCEPTED_BACKGROUND_COLOR;
      textColor = TEXT_COLOR;
    } 
    else if(appointment.isFromSubscribedSource) {
      bgColor = SOURCE_BACKGROUND_COLOR;
      textColor = SOURCE_TEXT_COLOR;
    } 
    else {
      bgColor = NORMAL_BACKGROUND_COLOR;
      textColor = TEXT_COLOR;
    }

    element.style.backgroundColor = bgColor;
    element.style.color = textColor;

    element.style.setProperty('--bg-color', bgColor);
    element.style.setProperty('--text-color', textColor);
  }


  showAppointmentsPopup = false;
  selectedAppointments: any[] = [];
  selectedDate: Date | null = null;

  onTooltipShowing(e: any): void {
    e.cancel = true;

    const clickedDate = new Date(e.appointments[0]?.appointmentData.startDate);
    this.selectedDate = clickedDate;

    this.selectedAppointments = (e.component.getDataSource().items() as any[])
      .filter(a => new Date(a.startDate).toDateString() === clickedDate.toDateString())
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  
    this.showAppointmentsPopup = true;
  }

  onClose = () => {
    this.showAppointmentsPopup = false;
  };

  getAppointmentBgColor(appointment: any): string {
    if (appointment.isAccepted) return ACCEPTED_BACKGROUND_COLOR;
    if (appointment.isFromSubscribedSource) return SOURCE_BACKGROUND_COLOR;

    return NORMAL_BACKGROUND_COLOR;
  }

  getAppointmentTextColor(appointment: any): string {
    if (appointment.isFromSubscribedSource) return SOURCE_TEXT_COLOR;

    return TEXT_COLOR;
  }

  onAppointmentClick(appointment: any) {
    this.router.navigate(['/my-trainings', 'training', appointment.id], {
      queryParams: { isFromCalendar: true },
    });
  }

  isRightEdge(appointmentData: any) {
    return this.isLastDayOfWeek(appointmentData.startDate); 
  }

  isLastDayOfWeek(date: Date): boolean {
    const d = new Date(date);
    const dayOfWeek = d.getDay();
    return dayOfWeek === 0;
  }
}
